import { assert, clamp, lerp } from '~/util';
import { defaultSampleAt, DELTA_T, waveformSamples } from '../waveform';
import type { WaveformApi } from '../waveform';
import type { InterpolatedWaveform } from '~/models/studio';
import type { Bounds, Vec2 } from '~/util';

export const wfInterpolated: WaveformApi<InterpolatedWaveform> = {
  samples: interpolatedSamples,
  sampleAt: defaultSampleAt,
  bounds: interpolatedBounds,
  points: interpolatedPoints,
  duration: interpolatedDuration,
  area: interpolatedArea
};

function interpolatedDuration(wf: InterpolatedWaveform): number {
  return wf.duration;
}

function interpolatedArea(wf: InterpolatedWaveform): number {
  return waveformSamples(wf).reduce((a, b) => a + b, 0) * DELTA_T;
}

/** approximate sum of all normalized samples */
function interpolatedSamples(wf: InterpolatedWaveform): number[] {
  return interpolation(wf).yp;
}

function interpolatedBounds(wf: InterpolatedWaveform): Bounds {
  if (wf.values.length === 0) {
    return { min: 0, max: 0 };
  }
  return { min: Math.min(...wf.values), max: Math.max(...wf.values) };
}

function interpolatedPoints(wf: InterpolatedWaveform, xOffset: number): Vec2[] {
  const interpolatedCurve = interpolation(wf);
  const result = interpolatedCurve.yp.map((yVal, i) => {
    return {
      x: xOffset + interpolatedCurve.xp[i] * wf.duration,
      y: yVal
    };
  });
  return result;
}

function interpolation(wf: InterpolatedWaveform) {
  assert(wf.times.length === wf.values.length);
  const xi: number[] = [];
  const yi: number[] = [];
  for (let i = 0; i < wf.times.length; i++) {
    if (i !== 0 && wf.times[i] === wf.times[i - 1]) {
      continue;
    }
    xi.push(wf.times[i]);
    yi.push(wf.values[i]);
  }
  const xMax = xi[xi.length - 1];
  const xMin = xi[0];
  const step = (xMax - xMin) / wf.duration;
  const xp: number[] = [];
  for (let x = xMin; x <= xMax; x += step) {
    xp.push(x);
  }
  if (yi.length <= 2) {
    //do linear interpolation
    const samples = Array<number>(wf.duration);
    if (yi.length == 0) {
      samples.fill(0, 0, wf.duration);
    } else if (yi.length === 1) {
      samples.fill(0, 0, yi[0]);
    } else {
      samples.fill(0, 0, wf.duration);
      // sample number
      let sampleNum = 0;
      const lastSample = wf.duration - 1;
      for (let i = 1; i < yi.length; i++) {
        const v0 = yi[i - 1];
        const v1 = yi[i];
        const t0 = clamp(wf.times[i - 1], 0, 1);
        const t1 = clamp(wf.times[i], 0, 1);
        const dur = t1 - t0;
        let t = sampleNum / lastSample;
        while (t <= t1) {
          const tInner = (t - t0) / dur;
          samples[sampleNum++] = lerp(v0, v1, tInner);
          t = sampleNum / lastSample;
        }
      }
    }
    return {
      yp: samples,
      xp: xp
    };
  }
  const yp = pchip(xi, yi, xp, false);
  return { yp, xp };
}

/**
 * Interpolate a set of points using a cubic Hermite spline.
 */
function pchip(
  xi: readonly number[],
  yi: readonly number[],
  xp: readonly number[],
  extrapolate: boolean
) {
  assert(xi.length === yi.length);
  const c = cubicHermiteSpline(xi, yi);
  let interval = 0;
  const out: number[] = [];
  for (let i = 0; i < xp.length; i++) {
    const xVal = xp[i];

    const newInterval = findInterval(xi, xVal, interval, extrapolate);
    assert(newInterval >= 0);
    interval = newInterval;

    out.push(evaluate(xVal - xi[interval], c, interval));
  }

  return out;
}

function cubicHermiteSpline(xi: readonly number[], yi: readonly number[]) {
  assert(xi.length === yi.length);
  /** Must be STRICTLY increasing */
  for (let i = 0; i < xi.length - 1; i++) {
    assert(xi[i + 1] > xi[i]);
  }
  const dydx = findDerivatives(xi, yi);
  assert(dydx.length === yi.length);
  const dx = Array.from<number>({ length: xi.length - 1 }).fill(0);
  const slope = Array.from<number>({ length: yi.length - 1 }).fill(0);
  const t = Array.from<number>({ length: yi.length - 1 }).fill(0);
  const c = Array.from<number[]>({ length: 4 }).map<number[]>(() => []);
  for (let i = 0; i < xi.length - 1; i++) {
    dx[i] = xi[i + 1] - xi[i];
    slope[i] = (yi[i + 1] - yi[i]) / dx[i];
    t[i] = (dydx[i] + dydx[i + 1] - 2 * slope[i]) / dx[i];
    c[0].push(t[i] / dx[i]);
    c[1].push((slope[i] - dydx[i]) / dx[i] - t[i]);
    c[2].push(dydx[i]);
    c[3].push(yi[i]);
  }

  return c;
}

function findInterval(
  xi: readonly number[],
  xVal: number,
  prevInterval: number,
  extrapolate: boolean
) {
  const a = xi[0];
  const b = xi[xi.length - 1];
  let interval = prevInterval;
  if (interval < 0 || interval >= xi.length - 1) {
    interval = 0;
  }

  if (xVal < a || xVal > b) {
    if (xVal < a && extrapolate) {
      interval = 0;
    } else if (xVal > b && extrapolate) {
      interval = xi.length - 2;
    } else {
      interval = -1;
    }
  } else if (xVal === b) {
    interval = xi.length - 2;
  } else {
    let low = 0;
    let high = interval;
    if (xVal >= xi[interval]) {
      low = interval;
      high = xi.length - 2;
    }

    if (xVal < xi[low + 1]) {
      high = low;
    }

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (xVal < xi[mid]) {
        high = mid;
      } else if (xVal >= xi[mid + 1]) {
        low = mid + 1;
      } else {
        low = mid;
        break;
      }
    }
    interval = low;
  }

  return interval;
}

function evaluate(s: number, c: number[][], ci: number) {
  let res = 0.0;
  let z = 1.0;
  const prefactor = 1.0;
  for (let i = 0; i < c.length; i++) {
    res = res + c[c.length - 1 - i][ci] * z * prefactor;
    z *= s;
  }
  return res;
}

function findDerivatives(xi: readonly number[], yi: readonly number[]) {
  assert(xi.length === yi.length);
  const dk = Array.from({ length: xi.length }, () => 0);
  const hk = Array.from({ length: xi.length - 1 }, () => 0);
  const mk = Array.from({ length: xi.length - 1 }, () => 0);
  const condition = Array.from({ length: mk.length - 1 }, () => false);
  const w1 = Array.from({ length: hk.length - 1 }, () => 0);
  const w2 = Array.from({ length: hk.length - 1 }, () => 0);
  const whmean = Array.from({ length: hk.length - 1 }, () => 0);

  for (let i = 0; i < xi.length - 1; i++) {
    hk[i] = xi[i + 1] - xi[i];
    mk[i] = (yi[i + 1] - yi[i]) / hk[i];
  }

  for (let i = 0; i < hk.length - 1; i++) {
    w1[i] = 2 * hk[i + 1] + hk[i];
    w2[i] = hk[i + 1] + 2 * hk[i];
    const signChange =
      (mk[i + 1] > 0 && mk[i] < 0) || (mk[i + 1] < 0 && mk[i] > 0);
    condition[i] = signChange || mk[i + 1] === 0 || mk[i] === 0;
  }

  for (let i = 0; i < w1.length; i++) {
    whmean[i] = (w1[i] / mk[i] + w2[i] / mk[i + 1]) / (w1[i] + w2[i]);
  }

  for (let i = 0; i < condition.length; i++) {
    dk[i + 1] = condition[i] ? 0 : 1 / whmean[i];
  }

  dk[0] = edgeCase(hk[0], hk[1], mk[0], mk[1]);
  dk[dk.length - 1] = edgeCase(
    hk[hk.length - 1],
    hk[hk.length - 2],
    mk[mk.length - 1],
    mk[mk.length - 2]
  );
  return dk;
}

function edgeCase(h0: number, h1: number, m0: number, m1: number) {
  const d = ((2 * h0 + h1) * m0 - h0 * m1) / (h0 + h1);
  const mask = (d > 0 && m0 < 0) || (d < 0 && m0 > 0);
  const mask2 =
    ((m0 > 0 && m1 < 0) || (m0 < 0 && m1 > 0)) &&
    Math.abs(d) > 3 * Math.abs(m0);
  const mmm = !mask && mask2;
  if (mask) {
    return 0;
  }
  if (mmm) {
    return 3 * m0;
  }
  return d;
}
